function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.updateCart.configure({ eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document });
  actions.openCart.configure({
    async handler() {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else window.location.href = Theme.routes.cart_url || '/cart';
    },
  });
}

const nativeFetch = window.fetch.bind(window);
const variantCache = new Map();
let cartCache = null;
let cartCacheAt = 0;

const toUrl = (resource) => {
  try { return new URL(resource instanceof Request ? resource.url : String(resource), location.origin); }
  catch { return null; }
};
const methodOf = (resource, init) => String(init?.method || (resource instanceof Request ? resource.method : 'GET')).toUpperCase();
const routePath = (route) => {
  try { return new URL(route, location.origin).pathname.replace(/\/$/, ''); }
  catch { return ''; }
};
const isCartJson = (url) => url?.origin === location.origin && url.pathname === `${routePath(Theme.routes.cart_url)}.json`;
const isCartAdd = (url) => url?.origin === location.origin && url.pathname === routePath(Theme.routes.cart_add_url);
const isCartChange = (url) => url?.origin === location.origin && url.pathname === routePath(Theme.routes.cart_change_url);
const isVariantRequest = (url) => url?.origin === location.origin && url.searchParams.has('option_values') && url.searchParams.has('section_id');

function stateFromSections(sections) {
  const source = sections && Object.values(sections).find((value) => typeof value === 'string' && value.includes('ref="cartItemCount"'));
  if (!source) return null;
  const html = new DOMParser().parseFromString(source, 'text/html');
  const itemCount = parseInt(html.querySelector('[ref="cartItemCount"]')?.textContent?.trim() || '0', 10);
  const quantities = new Map();
  html.querySelectorAll('cart-quantity-selector-component[data-variant-id]').forEach((selector) => {
    const id = Number(selector.getAttribute('data-variant-id'));
    const qty = Number(selector.querySelector('input[ref="quantityInput"]')?.value || 0);
    if (Number.isFinite(id) && Number.isFinite(qty)) quantities.set(id, (quantities.get(id) || 0) + qty);
  });
  return { itemCount: Number.isFinite(itemCount) ? itemCount : 0, quantities };
}

function cacheFromAdd(data) {
  const state = stateFromSections(data?.sections);
  if (!state) return;
  const added = Array.isArray(data?.items) ? data.items : data && typeof data === 'object' && (data.variant_id || data.id) ? [data] : [];
  const base = cartCache && Array.isArray(cartCache.items) ? structuredClone(cartCache) : {
    token: null, note: null, attributes: {}, original_total_price: 0, total_price: 0, total_discount: 0,
    total_weight: 0, item_count: 0, items: [], requires_shipping: true, currency: 'EUR',
    items_subtotal_price: 0, cart_level_discount_applications: [],
  };
  for (const item of added) {
    const id = Number(item.variant_id ?? item.id);
    if (!Number.isFinite(id)) continue;
    const index = base.items.findIndex((current) => Number(current.variant_id) === id);
    if (index >= 0) base.items[index] = { ...base.items[index], ...item };
    else base.items.push(item);
  }
  base.items = base.items.map((item) => {
    const qty = state.quantities.get(Number(item.variant_id ?? item.id));
    return qty === undefined ? item : { ...item, quantity: qty };
  }).filter((item) => Number(item.quantity) > 0);
  base.item_count = state.itemCount;
  cartCache = base;
  cartCacheAt = Date.now();
}

function setCartCache(data) {
  if (!data || !Array.isArray(data.items)) return;
  cartCache = data;
  cartCacheAt = Date.now();
}

window.fetch = async function(resource, init) {
  const url = toUrl(resource);
  const method = methodOf(resource, init);
  if (method === 'GET' && url && isVariantRequest(url) && variantCache.has(url.href)) {
    if (init?.signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
    return new Response(variantCache.get(url.href), { status: 200, headers: { 'Content-Type': 'text/html; charset=utf-8' } });
  }
  if (method === 'GET' && url && isCartJson(url) && cartCache && Date.now() - cartCacheAt < 300000) {
    return new Response(JSON.stringify(cartCache), { status: 200, headers: { 'Content-Type': 'application/json; charset=utf-8' } });
  }
  const response = await nativeFetch(resource, init);
  if (response.ok && url && method === 'POST' && (isCartAdd(url) || isCartChange(url))) {
    try {
      const data = await response.clone().json();
      if (isCartChange(url)) setCartCache(data);
      else cacheFromAdd(data);
    } catch {}
  }
  return response;
};

async function instantRemove() {
  await customElements.whenDefined('cart-items-component');
  const Ctor = customElements.get('cart-items-component');
  if (!Ctor || Ctor.prototype.__thylyInstantRemove) return;
  const original = Ctor.prototype.onLineItemRemove;
  Ctor.prototype.onLineItemRemove = function(line) {
    const row = this.refs?.cartItemRows?.[line - 1];
    const all = this.refs?.cartItemRows || [];
    const rows = row ? [row, ...all.filter((candidate) => candidate.dataset.parentKey === row.dataset.key)] : [];
    original.call(this, line);
    if (rows.length && rows.length < all.length) rows.forEach((candidate) => candidate?.remove());
  };
  Object.defineProperty(Ctor.prototype, '__thylyInstantRemove', { value: true });
}

async function warmVariant(url) {
  const normalized = toUrl(url);
  if (!normalized || !isVariantRequest(normalized) || variantCache.has(normalized.href)) return;
  try {
    const response = await nativeFetch(normalized.href, { credentials: 'same-origin' });
    if (response.ok) variantCache.set(normalized.href, await response.text());
  } catch {}
}

function warmPicker(picker) {
  if (!(picker instanceof HTMLElement) || picker.dataset.templateProductMatch !== 'true' || typeof picker.buildRequestUrl !== 'function') return;
  const urls = [];
  picker.querySelectorAll('input[data-option-value-id]').forEach((input) => {
    try {
      const url = picker.buildRequestUrl(input);
      if (url) urls.push(new URL(url, location.origin).href);
    } catch {}
  });
  [...new Set(urls)].slice(0, 8).forEach(warmVariant);
}

async function warmVariants() {
  await customElements.whenDefined('variant-picker');
  document.querySelectorAll('variant-picker[data-template-product-match="true"]').forEach(warmPicker);
}

if (window.Shopify?.actions) init();
else document.addEventListener('DOMContentLoaded', init, { once: true });
instantRemove().catch(() => {});

window.addEventListener('load', () => {
  setTimeout(() => nativeFetch(`${routePath(Theme.routes.cart_url)}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((response) => response.ok ? response.json() : null).then(setCartCache).catch(() => {}), 700);
  const schedule = window.requestIdleCallback || ((callback) => setTimeout(callback, 1200));
  schedule(() => warmVariants().catch(() => {}));
}, { once: true });

document.addEventListener('change', (event) => {
  const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
  if (picker) setTimeout(() => warmPicker(picker), 50);
}, true);
